import React, { memo, useCallback, useMemo, useRef, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { Dropdown } from 'primereact/dropdown'
import { InputSwitch } from 'primereact/inputswitch'
import { Link } from 'react-router-dom'
import { Tooltip } from 'primereact/tooltip'

import { SContainer, SLabelText } from '../../styles/Form'
import Text from '../../../ui/Text'
import { InfoIcon } from '../../../lib/icons/info-icon'
import { GoToIcon } from '../../../lib/icons/goto-icon'
import { theme } from '../../../lib/theme'
import { ChargePoint, ExternalServer } from '../../redux/api/web-api'
import { isSupportDomain } from '../../../lib/currentDomain'
import { getExternalServerLink } from '../../utils/chargePointUtils'
import { SEditSaveButton } from '../../components/ComponentTypeLabel'
import isOnline from '../../../lib/isOnline'
import useText from '../../hooks/useText'
import { confirm } from '../../uikit/ConfirmDialog'

export type ChargePointExternalServerWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    externalServersOptions: ExternalServer[] | undefined
    disableLoading: boolean
    disableCpms: boolean
    disabledPickerReason?: string
    pickerDescribedBy?: string
    disabledToggleCpms: boolean
    disabledToggleReason?: string
    enableLoading: boolean
    chargePoint: ChargePoint
    toggleOcppAvailability: (force?: boolean) => void
    updateExternalServer: (option?: number | null) => void | Promise<void>
}
export const ChargePointExternalServerWidget = memo(function ChargePointExternalServerWidget({
    className,
    style,
    enableLoading,
    disableLoading,
    disableCpms,
    disabledPickerReason,
    pickerDescribedBy,
    externalServersOptions,
    disabledToggleCpms,
    disabledToggleReason,
    toggleOcppAvailability,
    updateExternalServer,
    chargePoint,
}: ChargePointExternalServerWidgetProps): React.ReactElement {
    const text = useText()
    const [externalServerUpdating, setExternalServerUpdating] = useState(false)
    const externalServerBusy = externalServerUpdating || enableLoading || disableLoading
    const externalServerPickerWrapper = useRef<HTMLDivElement>(null)
    const externalServerToggleWrapper = useRef<HTMLSpanElement>(null)
    const confirmExternalServerToggle = useCallback(
        () =>
            confirm({
                header: text('lblChargerOfflineWarning'),
                message: text('lblChargerOfflineWarningProceed'),
                variant: 'danger',
            }),
        [text]
    )

    const serverOptions = externalServersOptions
        ? externalServersOptions &&
          externalServersOptions?.map((data) => ({
              label: `${data?.description}`,
              value: data?.id,
          }))
        : []

    const externalServerLink = useMemo(() => {
        if (!chargePoint?.additionalInfo?.externalServerId || !externalServersOptions) return

        return getExternalServerLink(chargePoint?.additionalInfo?.externalServerId, externalServersOptions)
    }, [chargePoint?.additionalInfo?.externalServerId, externalServersOptions])

    const toggleExternalServer = async () => {
        if (isOnline(chargePoint)) {
            toggleOcppAvailability()
            return
        }

        const confirmed = await confirmExternalServerToggle()
        if (confirmed) toggleOcppAvailability(true)
    }

    const selectExternalServer = async (externalServerId?: number | null) => {
        setExternalServerUpdating(true)

        try {
            await updateExternalServer(externalServerId ?? null)
        } finally {
            setExternalServerUpdating(false)
        }
    }

    return (
        <>
            <div className={cn(className)} style={style}>
                <SContainer>
                    {isSupportDomain && !chargePoint?.isOcpp && <Text code={'lblOcppProxy'}></Text>}
                    {isSupportDomain && !chargePoint?.isOcpp ? (
                        <SEditSaveButton>
                            <Tooltip content={'lblProxyRelayInfoText'} className="large-content">
                                <InfoIcon size={20} fill={theme.brandColors.CA_Blue} id="cpms-info-icon" />
                            </Tooltip>
                            <Link to="/externalservers" data-testid="external-server-redirect">
                                <GoToIcon color={theme.brandColors.CA_Blue} size={18} />
                            </Link>
                        </SEditSaveButton>
                    ) : null}
                </SContainer>
                {isSupportDomain && !chargePoint?.isOcpp ? (
                    <SLabelText data-testid="relay-label">
                        <Text code="lblOcppServer"></Text>
                    </SLabelText>
                ) : null}

                <div className="flex w-full align-items-center gap-2" data-testid="cpms-label">
                    {disabledPickerReason && (
                        <Tooltip target={externalServerPickerWrapper} content={disabledPickerReason} position="top" />
                    )}
                    <div
                        ref={externalServerPickerWrapper}
                        className="flex-1 min-w-0"
                        data-testid="external-server-picker-wrapper"
                        aria-busy={externalServerBusy}
                    >
                        <Dropdown
                            className="w-full"
                            aria-describedby={pickerDescribedBy}
                            data-testid={
                                chargePoint?.additionalInfo?.externalServerId
                                    ? `externalServer-select-${chargePoint?.additionalInfo?.externalServerId}`
                                    : 'externalServer-select'
                            }
                            id="externalServerId"
                            options={serverOptions}
                            value={chargePoint?.additionalInfo?.externalServerId}
                            placeholder={text('lblSelectOcppProxy')}
                            disabled={disableCpms || externalServerBusy}
                            filter
                            showClear
                            loading={externalServerBusy}
                            onChange={(option) => {
                                void selectExternalServer(option.value)
                            }}
                            pt={{
                                select: { 'data-testid': 'externalServer-select-input' },
                                clearIcon: { 'data-testid': 'externalServer-clear' },
                            }}
                        />
                    </div>

                    {disabledToggleCpms && disabledToggleReason && (
                        <Tooltip target={externalServerToggleWrapper} content={disabledToggleReason} position="top" />
                    )}
                    <span
                        ref={externalServerToggleWrapper}
                        className="inline-flex flex-shrink-0"
                        data-testid="external-server-toggle-wrapper"
                        aria-busy={externalServerBusy}
                    >
                        <InputSwitch
                            checked={chargePoint?.ocppEnabled ?? false}
                            onChange={toggleExternalServer}
                            data-testid="externalServer-toggle"
                            disabled={disabledToggleCpms || externalServerBusy}
                        />
                    </span>
                </div>

                {externalServerLink && (
                    <div data-testid="external-url" className="mt-2 text-600 text-sm">
                        <Text
                            code="lblExternalServerUrl"
                            values={{
                                externalServerUrl: externalServerLink,
                            }}
                        />
                    </div>
                )}
            </div>
        </>
    )
})
